import { useEffect, useState } from "react";
import api from "@/lib/api";
import { downloadText, todayYMD } from "@/lib/equi";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import AccountLinks from "@/components/AccountLinks";
import AccountSettings from "@/components/AccountSettings";
import { Save, Download, Trash2, Loader2, Stethoscope, Building2, ChevronRight } from "lucide-react";

const EMPTY = {
  farm_name: "",
  first_name: "",
  last_name: "",
  print_name_type: "farm",
  vet_name: "",
  vet_email: "",
  owner_email: "",
  cc_recipients: "",
};

const EMPTY_PROFILE = { practice_name: "", vet_name: "", vet_email: "", vet_phone: "" };

export default function Settings() {
  const { readOnly, isVet, activeFarm, refreshInvites } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [wipeOpen, setWipeOpen] = useState(false);
  const [wiping, setWiping] = useState(false);
  const [profile, setProfile] = useState(EMPTY_PROFILE);
  const [savingProfile, setSavingProfile] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [{ data }, prof] = await Promise.all([
          api.get("/settings"),
          isVet ? api.get("/vet/profile") : Promise.resolve(null),
        ]);
        setForm({ ...EMPTY, ...data });
        if (prof) setProfile({ ...EMPTY_PROFILE, ...prof.data });
      } catch {
        toast.error("Could not load settings");
      } finally {
        setLoading(false);
      }
    })();
  }, [isVet, activeFarm]);

  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const updProfile = (k) => (e) => setProfile((p) => ({ ...p, [k]: e.target.value }));

  const saveProfile = async () => {
    setSavingProfile(true);
    try {
      await api.put("/vet/profile", profile);
      toast.success("Practice info saved — it now prints on your reports");
    } catch {
      toast.error("Failed to save practice info");
    } finally {
      setSavingProfile(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/settings", form);
      toast.success("Settings saved");
    } catch {
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const exportAll = async () => {
    try {
      const { data } = await api.get("/export/all/csv");
      downloadText(`equimare-all-records-${todayYMD()}.csv`, data);
    } catch {
      toast.error("Export failed");
    }
  };

  const wipeSeason = async () => {
    setWiping(true);
    try {
      // Always download a backup first, then wipe.
      const { data } = await api.get("/export/all/csv");
      downloadText(`equimare-season-backup-${todayYMD()}.csv`, data);
      await api.delete("/season/records");
      toast.success("Season records cleared (backup downloaded)");
      setWipeOpen(false);
    } catch {
      toast.error("Failed to clear season");
    } finally {
      setWiping(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  const field = (key, label, placeholder = "", type = "text") => (
    <div className="space-y-1.5">
      <Label htmlFor={`s-${key}`}>{label}</Label>
      <Input id={`s-${key}`} type={type} value={form[key] || ""} onChange={upd(key)} placeholder={placeholder} data-testid={`settings-${key}`} />
    </div>
  );

  return (
    <div className="max-w-3xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight">Settings</h1>
        <p className="text-muted-foreground text-sm">
          {isVet ? "Your practice profile, per-farm report headers & data tools." : "Farm details, report header & data tools."}
        </p>
      </div>

      {/* Quick access to Client Farms (vet accounts) */}
      {isVet && (
        <button
          type="button"
          onClick={() => navigate("/dashboard/farms")}
          data-testid="settings-manage-farms-btn"
          className="w-full flex items-center gap-4 bg-card border border-border rounded-xl p-5 mb-6 text-left transition-shadow hover:shadow-lg hover:border-primary"
        >
          <div className="h-11 w-11 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
            <Building2 className="h-5 w-5 text-primary" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="font-bold text-base leading-tight">Manage Client Farms</p>
            <p className="text-sm text-muted-foreground">
              Add, edit or open client farms, and link to existing farm accounts.
            </p>
          </div>
          <ChevronRight className="h-5 w-5 text-muted-foreground shrink-0" />
        </button>
      )}

      {/* Practice profile (vet accounts) — prints on every report */}
      {isVet && (
        <div className="bg-card border border-border rounded-xl p-6 space-y-5 mb-6" data-testid="practice-profile-card">
          <div className="flex items-center gap-2">
            <Stethoscope className="h-5 w-5 text-primary" />
            <h2 className="font-bold text-lg">Your Practice</h2>
          </div>
          <p className="text-sm text-muted-foreground -mt-2">
            Shown in the top-right corner of every PDF report you generate for your client farms.
          </p>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="p-vet_name">Veterinarian Name</Label>
              <Input id="p-vet_name" value={profile.vet_name || ""} onChange={updProfile("vet_name")} placeholder="e.g. Dr. Jane Smith, DVM" data-testid="profile-vet_name" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="p-practice_name">Practice Name</Label>
              <Input id="p-practice_name" value={profile.practice_name || ""} onChange={updProfile("practice_name")} placeholder="e.g. Bluegrass Equine" data-testid="profile-practice_name" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="p-vet_email">Practice Email</Label>
              <Input id="p-vet_email" type="email" value={profile.vet_email || ""} onChange={updProfile("vet_email")} placeholder="e.g. office@bluegrass.vet" data-testid="profile-vet_email" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="p-vet_phone">Practice Phone</Label>
              <Input id="p-vet_phone" value={profile.vet_phone || ""} onChange={updProfile("vet_phone")} placeholder="e.g. (555) 123-4567" data-testid="profile-vet_phone" />
            </div>
          </div>
          <div className="flex justify-end">
            <Button onClick={saveProfile} disabled={savingProfile || readOnly} className="gap-2" data-testid="profile-save-btn">
              {savingProfile ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save Practice Info
            </Button>
          </div>
        </div>
      )}

      {!isVet && (
        <div className="mb-6">
          <AccountLinks role="farm" onChange={refreshInvites} />
        </div>
      )}

      {isVet && !activeFarm ? (
        <>
        <div className="bg-card border border-dashed border-border rounded-xl p-8 text-center mb-6" data-testid="settings-pick-farm-note">
          <Building2 className="h-8 w-8 text-muted-foreground mx-auto mb-3" />
          <p className="font-semibold">Select a client farm to edit its report header & run Season Cleanup</p>
          <p className="text-muted-foreground text-sm mt-1">
            Use the &ldquo;Working in&rdquo; switcher in the sidebar to choose a farm. Your practice info above applies to all farms.
          </p>
        </div>
        {!readOnly && <AccountSettings />}
        </>
      ) : (
      <>
      {/* Farm / report info */}
      <div className="bg-card border border-border rounded-xl p-6 space-y-5 mb-6">
        <h2 className="font-bold text-lg">{isVet ? `Report Header · ${activeFarm?.name || ""}` : "Farm & Report Header"}</h2>
        <div className="grid sm:grid-cols-2 gap-4">
          {field("farm_name", "Farm Name", "e.g. Green Acres Stud")}
          <div className="space-y-1.5">
            <Label>Print name on reports</Label>
            <Select value={form.print_name_type} onValueChange={(v) => setForm((f) => ({ ...f, print_name_type: v }))}>
              <SelectTrigger data-testid="settings-print_name_type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="farm">Farm name</SelectItem>
                <SelectItem value="person">Person name</SelectItem>
                <SelectItem value="none">None</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {field("first_name", "First Name", "e.g. Jane")}
          {field("last_name", "Last Name", "e.g. Doe")}
        </div>
      </div>

      {/* Vet / recipients */}
      <div className="bg-card border border-border rounded-xl p-6 space-y-5 mb-6">
        <h2 className="font-bold text-lg">Vet & Recipients</h2>
        <div className="grid sm:grid-cols-2 gap-4">
          {field("vet_name", "Vet Name", "e.g. Dr. Smith")}
          {field("vet_email", "Vet Email", "e.g. vet@clinic.com", "email")}
          {field("owner_email", "Owner Email", "e.g. owner@farm.com", "email")}
          {field("cc_recipients", "CC Recipients", "e.g. barn@farm.com, mgr@farm.com")}
        </div>
      </div>

      <div className="flex justify-end mb-8">
        <Button onClick={save} disabled={saving || readOnly} className="gap-2" data-testid="settings-save-btn">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save Settings
        </Button>
      </div>

      {/* Account (email & password) */}
      {!readOnly && (
        <div className="mb-6">
          <AccountSettings />
        </div>
      )}

      {/* Data tools */}
      <div className="bg-card border border-border rounded-xl p-6 space-y-4">
        <h2 className="font-bold text-lg">Data Tools</h2>
        <div className="flex flex-wrap items-center gap-3">
          <Button variant="outline" onClick={exportAll} className="gap-2" data-testid="settings-export-all-btn">
            <Download className="h-4 w-4" /> Export all records (CSV)
          </Button>
          <Button variant="outline" onClick={() => setWipeOpen(true)} disabled={readOnly} className="gap-2 text-destructive border-destructive/40 hover:bg-destructive/10" data-testid="settings-season-cleanup-btn">
            <Trash2 className="h-4 w-4" /> Season Cleanup
          </Button>
        </div>
        <p className="text-xs text-muted-foreground">
          Season Cleanup downloads a full CSV backup, then deletes all daily & teasing records for your farm. Your mare
          roster and settings are kept.
        </p>
      </div>
      </>
      )}

      <AlertDialog open={wipeOpen} onOpenChange={setWipeOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Clear this season&apos;s records?</AlertDialogTitle>
            <AlertDialogDescription>
              A CSV backup will download first. Then every daily and teasing record for your farm will be permanently
              deleted. Your mares and settings remain. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={wipeSeason} disabled={wiping} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="settings-wipe-confirm">
              {wiping && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Backup & Clear
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
